import { useEffect, useMemo, useState } from "react";
import { apiRequest } from "src/services/apiClient";
import { activeRows } from "src/components/GoaLineEditor/goaLineDraft";
import { findDuplicateCodes, type GoaCode } from "src/helpers/goaRules";
import type { ServicesByDay } from "./servicesRules";

// Step 4, story 15 (Pro): the GOÄ checker on the lines being created — POST /goa/validate runs the
// server's own rules (goaConflictService), the same ones behind the invoice page's GOÄ warning.
// Duplicate codes are found here instead, so they reach every plan the same way.

export type GoaConflict = { codes: [string, string]; note: string; date: string | null };
export type GoaMaxCountViolation = {
    nr: string;
    note: string;
    date: string | null;
    kind: "overLimit" | "missingRequiredCode";
    maxCount?: number;
    billedCount?: number;
    requiresAnyOf?: string[];
};

/** A per-code rule's result (backend services/goaBillingCheck.ts): the type picks the text. */
export type GoaBillingFinding = {
    key: string;
    type: string;
    date: string | null;
    codes: string[];
    params: Record<string, string | number>;
    ref: string;
    source: string;
};

export type GoaValidation = {
    conflicts: GoaConflict[];
    maxCountViolations: GoaMaxCountViolation[];
    billing?: { findings: GoaBillingFinding[]; coveredCodes: string[] };
};

/** One thing the practice has to look at and confirm before creating. */
export type GoaFinding =
    | { key: string; kind: "duplicate"; date: string | null; nr: string; count: number; note: string }
    | { key: string; kind: "singleRate"; date: string | null; nr: string; factor: number; note: string }
    | { key: string; kind: "rule"; type: string; date: string | null; codes: string[]; params: Record<string, string | number>; note: string }
    | { key: string; kind: "conflict"; date: string | null; codes: [string, string]; note: string }
    | { key: string; kind: "overLimit"; date: string | null; nr: string; billed: number; max: number; note: string }
    | { key: string; kind: "missingRequiredCode"; date: string | null; nr: string; requires: string[]; note: string };

/** The lines the check needs — treatment days only, coded rows only. */
export function goaCheckPayload(services: ServicesByDay, days: string[]) {
    return days.flatMap((date) =>
        activeRows(services[date] ?? [])
            .filter((r) => r.code.trim())
            .map((r) => ({ date, code: r.code.trim(), quantity: r.quantity, ...(r.justification.trim() ? { justification: r.justification.trim() } : {}) })),
    );
}

/** Findings with keys that stay the same while the problem stays the same — a confirmation is kept
 *  across unrelated edits, and asked for again when the problem itself changes. */
export function goaFindings(result: GoaValidation): GoaFinding[] {
    const out: GoaFinding[] = [];
    for (const c of result.conflicts) {
        out.push({ key: `c|${c.date}|${c.codes.join("+")}`, kind: "conflict", date: c.date, codes: c.codes, note: c.note });
    }
    for (const v of result.maxCountViolations) {
        if (v.kind === "overLimit") {
            out.push({ key: `m|${v.date}|${v.nr}|${v.billedCount}`, kind: "overLimit", date: v.date, nr: v.nr, billed: v.billedCount ?? 0, max: v.maxCount ?? 0, note: v.note });
        } else {
            out.push({ key: `r|${v.date}|${v.nr}`, kind: "missingRequiredCode", date: v.date, nr: v.nr, requires: v.requiresAnyOf ?? [], note: "" });
        }
    }
    for (const f of result.billing?.findings ?? []) {
        out.push({ key: `b|${f.key}`, kind: "rule", type: f.type, date: f.date, codes: f.codes, params: f.params, note: `${f.ref}: ${f.source}` });
    }
    return out.sort(byDate);
}

const byDate = (a: GoaFinding, b: GoaFinding) => (a.date ?? "").localeCompare(b.date ?? "");

/** The same code on several lines of a day — on every plan, without the server. The key holds the
 *  line count: one more line of the code has to be confirmed again. */
export function duplicateFindings(services: ServicesByDay, days: string[]): GoaFinding[] {
    return days.flatMap((date) => {
        const rows = activeRows(services[date] ?? []);
        return [...findDuplicateCodes(rows)].map((nr): GoaFinding => {
            const count = rows.filter((r) => r.code === nr).length;
            return { key: `d|${date}|${nr}|${count}`, kind: "duplicate", date, nr, count, note: "" };
        });
    });
}

/** A single-rate code above factor 1 — on every plan, like duplicates. The key holds the factor. */
export function singleRateFindings(services: ServicesByDay, days: string[], catalogByNr: Map<string, GoaCode>): GoaFinding[] {
    return days.flatMap((date) =>
        activeRows(services[date] ?? [])
            .filter((r) => catalogByNr.get(r.code.trim())?.singleRate && r.factor > 1)
            .map((r): GoaFinding => ({ key: `s|${date}|${r.code.trim()}|${r.factor}`, kind: "singleRate", date, nr: r.code.trim(), factor: r.factor, note: "" })),
    );
}

export type GoaCheck =
    | { status: "off" }
    | { status: "loading" }
    | { status: "error" }
    | { status: "done"; findings: GoaFinding[]; coveredCodes: string[] };

/** Everything to confirm in step 4. A code over its limit is already the sharper finding for those
 *  lines, and a code with a rule of its own is covered by that rule, so neither gets a duplicate
 *  finding on top — a repeat within the rules passes without a tick. */
export function allFindings(services: ServicesByDay, days: string[], check: GoaCheck, catalogByNr: Map<string, GoaCode> = new Map()): GoaFinding[] {
    const server = check.status === "done" ? check.findings : [];
    const overLimit = new Set(server.flatMap((f) => (f.kind === "overLimit" ? [`${f.date}|${f.nr}`] : [])));
    const covered = new Set(check.status === "done" ? check.coveredCodes : []);
    const duplicates = duplicateFindings(services, days).filter((f) => f.kind !== "duplicate" || (!overLimit.has(`${f.date}|${f.nr}`) && !covered.has(f.nr)));
    return [...duplicates, ...singleRateFindings(services, days, catalogByNr), ...server].sort(byDate);
}

/** Runs the check whenever `enabled` and the coded lines change; `off` when not enabled. */
export function useGoaDraftCheck(services: ServicesByDay, days: string[], enabled: boolean, who: { patientId?: string | null; doctorId?: string | null } = {}): GoaCheck {
    const payload = useMemo(
        () => JSON.stringify({
            services: goaCheckPayload(services, days),
            ...(who.patientId ? { patientId: who.patientId } : {}),
            ...(who.doctorId ? { doctorId: who.doctorId } : {}),
        }),
        [services, days, who.patientId, who.doctorId],
    );
    const [state, setState] = useState<{ payload: string; check: GoaCheck } | null>(null);

    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;
        setState({ payload, check: { status: "loading" } });
        apiRequest<GoaValidation>(
            "/goa/validate",
            { method: "POST", body: payload },
            { "Content-Type": "application/json" },
        )
            .then((res) => !cancelled && setState({ payload, check: { status: "done", findings: goaFindings(res), coveredCodes: res.billing?.coveredCodes ?? [] } }))
            .catch(() => !cancelled && setState({ payload, check: { status: "error" } }));
        return () => {
            cancelled = true;
        };
    }, [enabled, payload]);

    if (!enabled) return { status: "off" };
    // An answer for other lines than the current ones is stale — still checking.
    return state && state.payload === payload ? state.check : { status: "loading" };
}
